import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Calendar, MapPin, Phone, Mail, Car, Copy, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstallerView = () => {
  const { linkSlug } = useParams();
  const [installer, setInstaller] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInstallerJobs();
  }, [linkSlug]);

  const fetchInstallerJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/installer/${linkSlug}/jobs`);
      setInstaller(response.data.installer);
      setJobs(response.data.jobs);
    } catch (error) {
      console.error("Error fetching installer jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const copyPhoneNumber = (phone, customerName) => {
    navigator.clipboard.writeText(phone)
      .then(() => {
        toast.success(`Phone number for ${customerName} copied!`);
      })
      .catch(() => {
        toast.error("Failed to copy phone number");
      });
  };

  const formatInstallDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit"
    });
  };

  const openSMSApp = (phone, customerName, installDate) => {
    const formattedDate = formatInstallDate(installDate);
    const message = `Hey this is ${installer?.name || 'your installer'}, I will be installing your battery ${formattedDate} at around 9am`;
    
    // Detect if iOS device
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    
    // Create SMS link (works on both mobile and desktop)
    const smsLink = `sms:${phone}${isIOS ? '&' : '?'}body=${encodeURIComponent(message)}`;
    
    // Create temporary link and click it
    const link = document.createElement('a');
    link.href = smsLink;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show confirmation
    toast.success("Opening SMS app...");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 mt-4">Loading jobs...</p>
        </div>
      </div>
    );
  }

  if (!installer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-900 font-bold">Installer not found</p>
          <p className="text-gray-500 mt-2">This link may be invalid or expired</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome, {installer.name}
            </h1>
            <p className="text-gray-600 mt-1">
              Your assigned jobs for Hybrid2Go
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Job Count */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-blue-700">Total Jobs Assigned</div>
              <div className="text-4xl font-bold text-blue-900 mt-1">{jobs.length}</div>
            </div>
            <Calendar className="w-12 h-12 text-blue-500" />
          </div>
        </div>

        {/* Jobs List */}
        {jobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-900 font-bold">No jobs assigned yet</p>
            <p className="text-gray-500 mt-2">Check back later for new assignments</p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className={`bg-white rounded-lg shadow-sm border-2 p-6 transition-colors ${
                  job.customer_contacted_at 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {/* Job Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {job.name} {job.last_name}
                      </h2>
                      {job.customer_contacted_at && (
                        <span className="px-3 py-1 bg-green-600 text-white rounded-full text-sm font-medium flex items-center gap-1">
                          ✓ Customer Contacted
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-base text-gray-700 mt-2 font-medium">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        Install: {formatDate(job.install_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Car className="w-5 h-5 text-blue-600" />
                        {job.vehicle_year} {job.vehicle_make} {job.vehicle_model}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Compliance Status */}
                {job.customer_contacted_at && (
                  (() => {
                    const installDate = new Date(job.install_date);
                    const contactedDate = new Date(job.customer_contacted_at);
                    const hoursAdvance = (installDate - contactedDate) / (1000 * 60 * 60);
                    const isCompliant = hoursAdvance >= 12;
                    
                    return (
                      <div className={`mb-4 p-3 rounded-lg border-2 ${
                        isCompliant 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${
                            isCompliant ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {isCompliant 
                              ? `✓ Compliant - Contacted ${Math.floor(hoursAdvance)} hours before appointment` 
                              : `⚠ Policy Violation - Only ${Math.floor(hoursAdvance)} hours notice (12 hour minimum required)`
                            }
                          </span>
                        </div>
                      </div>
                    );
                  })()
                )}
                {!job.customer_contacted_at && job.list_sent_at && (
                  <div className="mb-4 p-3 rounded-lg border-2 bg-yellow-50 border-yellow-200">
                    <div className="text-sm font-bold text-yellow-700">
                      ⏰ Action Required: Contact customer at least 12 hours before appointment
                    </div>
                  </div>
                )}

                {/* Customer Info - Compact */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-gray-900 mb-2">
                        <Phone className="w-5 h-5 text-blue-600" />
                        <span className="font-bold text-lg">{job.phone}</span>
                      </div>
                      <div className="flex items-start gap-2 text-gray-700">
                        <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <div className="font-medium">{job.address}</div>
                          <div>{job.city}, {job.state} {job.zip}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-700 mb-2">
                        <span className="font-medium">Service:</span> {job.service}
                      </div>
                      <div className="text-gray-700 mb-2">
                        <span className="font-medium">Type:</span> {job.refurb_or_new}
                      </div>
                      {job.notes && (
                        <div className="text-gray-700 bg-yellow-50 p-2 rounded border border-yellow-200">
                          <span className="font-medium">⚠️ Note:</span> {job.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {!job.customer_contacted_at ? (
                  <div className="space-y-3">
                    <button
                      onClick={() => openSMSApp(job.phone, job.name, job.install_date)}
                      className="w-full py-4 bg-blue-600 text-white font-bold text-lg rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 shadow-lg"
                    >
                      <MessageSquare className="w-6 h-6" />
                      Text Customer
                    </button>
                    
                    <button
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/bookings/${job.id}/mark-contacted`);
                          toast.success('Customer contact recorded!');
                          fetchInstallerJobs();
                        } catch (error) {
                          console.error('Error marking contacted:', error);
                          toast.error('Failed to record contact');
                        }
                      }}
                      className="w-full py-3 bg-white border-2 border-green-600 text-green-700 font-medium rounded-lg hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
                    >
                      ✓ Mark as Contacted
                    </button>
                  </div>
                ) : (
                  <div className="py-4 bg-green-100 text-green-800 font-bold text-lg rounded-lg flex items-center justify-center gap-2 border-2 border-green-300">
                    <span>✓ Completed - Customer Contacted</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-gray-500">
        <p>Hybrid2Go - Installer Portal</p>
      </div>
    </div>
  );
};

export default InstallerView;
